import React from 'react';
import { FiBarChart2, FiCode, FiCopy } from 'react-icons/fi';
import { WidgetDef } from '../types';
import { CopyButton, DemoHead, copyText, useWidgetSite } from '../shared';

// spec 621:3926 — no settings: the whole block or one code per field, both spelled out under the preview
interface Field {
    label: string;
    // the label inside the whole block ("Shares on Issue: ")
    blockLabel: string;
    cls: string;
    value: string;
    up?: boolean;
    // extra attribute on the whole block's value div
    attr?: string;
}

const F: Record<string, Field> = {
    symbol: { label: 'Symbol', blockLabel: 'Symbol: ', cls: 'investi-share-price-symbol', value: '' },
    last: { label: 'Share price', blockLabel: 'Share Price: ', cls: 'investi-share-price-last', value: 'A$0.245', attr: ' investi-num-decimal-places="3"' },
    marketCap: { label: 'Market cap', blockLabel: 'Market Cap: $', cls: 'investi-market-cap', value: 'A$45,673,000' },
    shares: { label: 'Shares on issue', blockLabel: 'Shares on Issue: ', cls: 'investi-shares-on-issue', value: '186,420,331' },
    pct: { label: '% change', blockLabel: '% Change: ', cls: 'investi-share-price-movement-percent', value: '▲ 4.3%', up: true },
    move: { label: '$ change', blockLabel: '$ Change: ', cls: 'investi-share-price-movement', value: '+A$0.010', up: true },
    volume: { label: 'Volume', blockLabel: 'Volume: ', cls: 'investi-share-price-volume', value: '659,000' },
    prevClose: { label: 'Prev close', blockLabel: 'Prev Close: ', cls: 'investi-share-price-prevClose', value: 'A$0.235' },
    low: { label: 'Low', blockLabel: 'Low: ', cls: 'investi-share-price-low', value: 'A$0.232' },
    high: { label: 'High', blockLabel: 'High: ', cls: 'investi-share-price-high', value: 'A$0.250' },
    low52: { label: '52 week low', blockLabel: '52 Week Low: ', cls: 'investi-share-price-low52Week', value: 'A$0.115' },
    high52: { label: '52 week high', blockLabel: '52 Week High: ', cls: 'investi-share-price-high52Week', value: 'A$0.310' },
    bid: { label: 'Bid', blockLabel: 'Bid: ', cls: 'investi-share-price-bid', value: 'A$0.244' },
    ask: { label: 'Ask', blockLabel: 'Ask: ', cls: 'investi-share-price-ask', value: 'A$0.246' },
    open: { label: 'Open', blockLabel: 'Open: ', cls: 'investi-share-price-open', value: 'A$0.235' },
    updated: { label: 'Updated', blockLabel: 'Updated: ', cls: 'investi-share-price-updated', value: '3 Sep 2026, 2:41 pm' },
    options: { label: 'Unlisted options', blockLabel: 'Unlisted Options: ', cls: 'investi-unlisted-options', value: '12,500,000' },
    rights: { label: 'Performance rights', blockLabel: 'Performance Rights: ', cls: 'investi-performance-rights', value: '4,200,000' },
    escrowed: { label: 'Escrowed shares', blockLabel: 'Escrowed Shares: ', cls: 'investi-escrowed-shares', value: '8,400,000' },
    top20: { label: 'Top 20 hold', blockLabel: 'Top 20 Hold: ', cls: 'investi-top20-percent', value: '67.9%' },
};

// the order each part of the spec lists them in
const DEMO_ORDER = ['symbol', 'last', 'marketCap', 'shares', 'pct', 'move', 'volume', 'prevClose', 'low', 'low52', 'high', 'high52', 'ask', 'open', 'bid', 'updated'];
const BLOCK_ORDER = ['symbol', 'last', 'marketCap', 'shares', 'pct', 'move', 'volume', 'prevClose', 'low', 'high', 'low52', 'high52', 'bid', 'ask', 'open', 'updated'];
const FIELD_ORDER = ['last', 'marketCap', 'shares', 'pct', 'move', 'volume', 'prevClose', 'open', 'low', 'high', 'low52', 'high52', 'bid', 'ask', 'symbol', 'updated'];
const SUPPLIED = ['options', 'rights', 'escrowed', 'top20'];

const fieldCode = (f: Field) => `<div class="${f.cls}"></div>`;
const blockLines = (f: Field) => [`  <div class="investi-market-data-field-name">${f.blockLabel}</div>`, `  <div class="${f.cls}"${f.attr ?? ''}></div>`];

const BLOCK_A = ['<div class="investi-market-data-container">', ...BLOCK_ORDER.slice(0, 8).flatMap(k => blockLines(F[k]))].join('\n');
const BLOCK_B = [...BLOCK_ORDER.slice(8).flatMap(k => blockLines(F[k])), '</div>'].join('\n');
// name and value side by side, the values lined up
const NAME_WIDTH = Math.max(...SUPPLIED.map(k => blockLines(F[k])[0].length)) + 1;
const BLOCK_SUPPLIED = SUPPLIED.map(k => blockLines(F[k])).map(([n, v]) => n.padEnd(NAME_WIDTH) + v.trim()).join('\n');
// what Copy the Whole Block puts on the clipboard: the four typed-in fields go inside the same container
const WHOLE_BLOCK = [BLOCK_A, ...BLOCK_ORDER.slice(8).flatMap(k => blockLines(F[k])), ...SUPPLIED.flatMap(k => blockLines(F[k])), '</div>'].join('\n');

const DemoCell: React.FC<{ f: Field; value?: string }> = ({ f, value }) =>
    <div className="cell">
        <span className="l">{f.label}</span>
        <span className={`v${f.up ? ' up' : ''}`}>{value ?? f.value}</span>
        <button type="button" className="cp" title={`Copy the ${f.label} code`} onClick={() => copyText(fieldCode(f), `${f.label} code`)}><FiCopy /></button>
    </div>;

const SectionHead: React.FC<{ title: string; sub: string }> = ({ title, sub, children }) =>
    <div className="wd-cs-head">
        <span className="icon-tile"><FiCode /></span>
        <div className="t"><h2>{title}</h2><p>{sub}</p></div>
        {children}
    </div>;

const FieldRow: React.FC<{ f: Field; supplied?: boolean }> = ({ f, supplied }) =>
    <div className={`row${supplied ? ' supplied' : ''}`}>
        <span className="l">{f.label}</span>
        <code>{fieldCode(f)}</code>
        <CopyButton text={fieldCode(f)} what={`${f.label} code`} />
    </div>;

const After: React.FC = () => <>
    <section className="card wd-cs-section">
        <SectionHead title="Option 1 — the Whole Block" sub="Every field in one go. Paste it, then delete any line you do not want.">
            <button type="button" className="btn-outline small with-icon" onClick={() => copyText(WHOLE_BLOCK, 'whole block')}><FiCopy /> Copy the Whole Block</button>
        </SectionHead>
        <div className="wd-cs-panels">
            <div className="wd-cs-panel"><small>From the ASX feed</small><pre>{BLOCK_A}</pre></div>
            <div className="wd-cs-panel"><small>…continued</small><pre>{BLOCK_B}</pre></div>
        </div>
        <div className="wd-cs-panel extra"><small>And the four you type in yourself — paste inside the same container</small><pre>{BLOCK_SUPPLIED}</pre></div>
        <p className="wd-muted">The delayed-price notice is added for you: <code>{'<div class="investi-market-data-delayed-info">Market Data prices are at least 20 minutes delayed</div>'}</code></p>
    </section>

    <section className="card wd-cs-section">
        <SectionHead title="Option 2 — One Code per Field" sub="Drop a single figure anywhere on the page — in a header, a sidebar, or beside a paragraph. Copy just the line you need." />
        <div className="wd-cs-fields">{FIELD_ORDER.map(k => <FieldRow key={k} f={F[k]} />)}</div>
        <div className="wd-label">Supplied by the company — typed in on the Investi platform</div>
        <div className="wd-cs-fields">{SUPPLIED.map(k => <FieldRow key={k} f={F[k]} supplied />)}</div>
        <div className="wd-cs-notes">
            <p className="wd-muted">These four are not on the ASX feed — you type them in under Website Hub › Website content › Capital structure, and the widget shows whatever is saved there.</p>
            <p className="wd-muted">Wrap a field in your own markup to label it — for example <code>{'<span>Market cap:</span>'}</code> then the field div. The number is filled in when the page loads.</p>
        </div>
    </section>
</>;

const capitalStructure: WidgetDef = {
    slug: 'capital-structure',
    name: 'Capital structure',
    description: 'Shares on issue, options and market cap',
    icon: <FiBarChart2 />,
    title: 'Capital Structure Widget',
    installed: true,
    page: '/investors/investors-dashboard/',
    views: 1240,
    getCodeButton: true,
    demoPath: '/investors/investors-dashboard/',
    addBlocks: [
        { title: '1 · WordPress shortcode', code: '[investi_market_data instance="capital-structure"]' },
        {
            title: '2 · Or the HTML',
            options: [
                { title: 'Option 1 — the whole block', hint: 'every field in one paste' },
                { title: 'Option 2 — one code per field', hint: 'just the figures you want' },
            ],
            after: 'Both are under the preview.',
        },
    ],
    After,
    Demo: () => {
        const { name, ticker } = useWidgetSite();
        return <div className="wd-cs">
            <DemoHead title="Capital structure and market data" sub={`${name} · ASX: ${ticker} · as at 3 September 2026`} />
            <div className="wd-cs-table">
                {DEMO_ORDER.map(k => <DemoCell key={k} f={F[k]} value={k === 'symbol' ? ticker : undefined} />)}
            </div>
            <div className="wd-label">Supplied by the company</div>
            <div className="wd-cs-table">{SUPPLIED.map(k => <DemoCell key={k} f={F[k]} />)}</div>
            <p className="muted small">Market data prices are at least 20 minutes delayed</p>
        </div>;
    },
};

export default capitalStructure;
